// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import api from './client';

export interface Club {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  country: string;
  default_currency?: string;
  organisation_id?: string;
  visibility?: string;
}

// Alias for PublicDirectory compatibility
export type PublicClub = Club;

export interface DirectoryResponse {
  clubs: Club[];
  total: number;
}

export interface SearchParams {
  q?: string;
  country?: string;
  organisation_id?: string;
  limit?: number;
  offset?: number;
}

/**
 * Search clubs in the public directory.
 *
 * Note: Currently does client-side filtering by name since backend doesn't support `q` parameter yet.
 * See story F15 for backend search support to improve performance with large directories.
 */
export async function searchClubs(params: SearchParams): Promise<{ data: DirectoryResponse }> {
  // Client-side filtering workaround until backend supports q parameter (F15)
  const response = await api.get<DirectoryResponse>('/clubs/directory', {
    params: {
      country: params.country,
      organisation_id: params.organisation_id,
      limit: params.limit || 100, // Fetch more to allow client-side filtering
      offset: params.offset || 0,
    },
  });

  // Client-side filtering by name if q parameter is provided
  if (params.q && params.q.trim().length > 0) {
    const query = params.q.toLowerCase().trim();
    const filteredClubs = response.data.clubs.filter((club) =>
      club.name.toLowerCase().includes(query)
    );
    
    return {
      data: {
        clubs: filteredClubs.slice(0, params.limit || 10),
        total: filteredClubs.length,
      },
    };
  }

  return {
    data: {
      clubs: response.data.clubs.slice(0, params.limit || 10),
      total: response.data.total,
    },
  };
}

/**
 * Fetch public clubs from the directory (for PublicDirectory page).
 * This is a wrapper around searchClubs without the search query parameter.
 */
export async function fetchPublicClubs(params: Omit<SearchParams, 'q'>): Promise<DirectoryResponse> {
  const response = await searchClubs(params);
  return response.data;
}
